import React, { useRef, useEffect, useCallback } from 'react';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const ITEM_HEIGHT = 44;
const VISIBLE_ITEMS = 5;
const CENTER_INDEX = Math.floor(VISIBLE_ITEMS / 2);

interface ScrollColumnProps {
  items: string[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

function ScrollColumn({ items, selectedIndex, onSelect }: ScrollColumnProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const scrollToIndex = useCallback((index: number, smooth = true) => {
    if (!containerRef.current) return;
    const top = index * ITEM_HEIGHT;
    containerRef.current.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  useEffect(() => {
    scrollToIndex(selectedIndex, false);
  }, [selectedIndex, scrollToIndex]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    isScrollingRef.current = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (!containerRef.current) return;
      const scrollTop = containerRef.current.scrollTop;
      const index = Math.round(scrollTop / ITEM_HEIGHT);
      const clamped = Math.max(0, Math.min(items.length - 1, index));
      scrollToIndex(clamped, true);
      if (clamped !== selectedIndex) onSelect(clamped);
      isScrollingRef.current = false;
    }, 80);
  };

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="relative overflow-y-auto scrollbar-none snap-y snap-mandatory"
      style={{
        height: ITEM_HEIGHT * VISIBLE_ITEMS,
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Top padding */}
      <div style={{ height: ITEM_HEIGHT * CENTER_INDEX }} />

      {items.map((item, i) => {
        const isSelected = i === selectedIndex;
        return (
          <div
            key={i}
            className="snap-center flex items-center justify-center cursor-pointer select-none"
            style={{ height: ITEM_HEIGHT }}
            onClick={() => {
              onSelect(i);
              scrollToIndex(i);
            }}
          >
            <span
              className={
                isSelected
                  ? 'text-base font-bold text-slate-900'
                  : 'text-sm font-medium text-slate-300'
              }
            >
              {item}
            </span>
          </div>
        );
      })}

      {/* Bottom padding */}
      <div style={{ height: ITEM_HEIGHT * CENTER_INDEX }} />
    </div>
  );
}

interface ScrollDatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
}

export default function ScrollDatePicker({ value, onChange }: ScrollDatePickerProps) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const minYear = 1920;
  const years = Array.from({ length: currentYear - minYear + 1 }, (_, i) => minYear + i);

  // Parse initial value
  const parsed = value ? value.split('-').map(Number) : [currentYear - 30, 1, 1];
  const [selYear, selMonth, selDay] = [parsed[0], parsed[1] - 1, parsed[2]];

  const yearIndex = years.indexOf(selYear) >= 0 ? years.indexOf(selYear) : years.length - 31;
  const monthIndex = selMonth >= 0 && selMonth < 12 ? selMonth : 0;

  const daysInMonth = new Date(selYear, selMonth + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const dayIndex = Math.min(selDay - 1, daysInMonth - 1);

  const emitChange = (y: number, m: number, d: number) => {
    const maxDay = new Date(y, m + 1, 0).getDate();
    const clampedDay = Math.min(d, maxDay);
    const yStr = String(y);
    const mStr = String(m + 1).padStart(2, '0');
    const dStr = String(clampedDay).padStart(2, '0');
    onChange(`${yStr}-${mStr}-${dStr}`);
  };

  return (
    <div className="relative">
      {/* Selection highlight bar */}
      <div
        className="absolute left-2 right-2 rounded-xl bg-slate-100 pointer-events-none z-0"
        style={{
          top: ITEM_HEIGHT * CENTER_INDEX,
          height: ITEM_HEIGHT,
        }}
      />

      <div className="relative z-10 flex">
        {/* Month */}
        <div className="flex-[1.4]">
          <ScrollColumn
            items={MONTHS}
            selectedIndex={monthIndex}
            onSelect={(i) => emitChange(years[yearIndex], i, days[dayIndex] ?? 1)}
          />
        </div>
        {/* Day */}
        <div className="flex-[0.6]">
          <ScrollColumn
            items={days.map(String)}
            selectedIndex={dayIndex >= 0 ? dayIndex : 0}
            onSelect={(i) => emitChange(years[yearIndex], monthIndex, i + 1)}
          />
        </div>
        {/* Year */}
        <div className="flex-1">
          <ScrollColumn
            items={years.map(String)}
            selectedIndex={yearIndex}
            onSelect={(i) => emitChange(years[i], monthIndex, days[dayIndex] ?? 1)}
          />
        </div>
      </div>
    </div>
  );
}
